import React, { useEffect, useMemo, useState } from 'react';

import { Flex, Table, Tbody, Td, useTheme } from '@chakra-ui/react';
import { Collapse, TableContainer } from '@material-ui/core';
import {
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useSelectCompetenciesSelectedGroupBy } from 'Deer/intent/Competencies/slice/CompetenciesIntentSlice';
import useLocalStorage from 'components/hooks/useLocalStorage';
import { useSelectedView } from 'reducers/viewsSlice';
import { Loader } from 'screens/analytics/commonComponents';
import styled from 'styled-components';
import { checkIntentLandingPagesFeatureEnabled } from 'utils/helpers/featureFlags';

import { localStorageKeys } from 'constants/localStorageKeys';

import { TableBodyRow, TableHeaderRow } from 'uiToolkitV1/TableComponents';
import { TablePagination } from 'uiToolkitV1/TableComponents/Pagination';
import { TableHeaderCell } from 'uiToolkitV1/TableComponents/TableHeader';
import { TableHead } from 'uiToolkitV1/TableComponents/TableHeader/TableHead';

import { useLevelUpAnalytics } from '../context';
import { columnToCellMetaMapper, getFlexJustifyFromCellAlign } from '../helpers';
import { useFetchLevelUpAnalyticsData } from '../hooks/dataHooks';
import SubGroupedByEmployeesTable from './SubGroupedByEmployeesTable';

const defaultRowsPerPage = 10;

const groupedByEmployeesTableRowsPerPage = 'groupedByEmployeesTableRowsPerPage';

export const defaultRowsPerPageForLevelUp = {
  [groupedByEmployeesTableRowsPerPage]: defaultRowsPerPage,
};

const GroupedByEmployeesTable = () => {
  const {
    state: {
      currentState: { selectedGroupByKey, selectedSplitByKey, selectedDateRange },
      selectedView: { expandedRowDetails },
    },
    setNoData,
  } = useLevelUpAnalytics();

  const theme = useTheme();

  const sideNavSelectedCohort = useSelectedView();

  const selectedGroupByValue = useSelectCompetenciesSelectedGroupBy(sideNavSelectedCohort?.id);

  const groupBy = checkIntentLandingPagesFeatureEnabled() ? selectedGroupByValue : selectedGroupByKey;

  const { data, isLoading, isIdle } = useFetchLevelUpAnalyticsData({
    cohort: sideNavSelectedCohort,
    groupByKeys: groupBy,
    splitByKeys: selectedSplitByKey,
    dateRange: selectedDateRange,
    isNewCompetencyEnabled: true,
  });

  const [rowsPerPageLS, setRowsPerPageLS] = useLocalStorage(
    localStorageKeys.analyticsLevelUpTableFilters,
    defaultRowsPerPageForLevelUp
  );

  const [tableContainerWidth, setTableContainerWidth] = useState(1600);

  useEffect(() => {
    if (!isLoading && !isIdle) {
      // setData(data?.values);
      if (!data?.values?.length) {
        setNoData(true);
      }
    }
  }, [isLoading, data, isIdle]);

  const [expanded, setExpanded] = useState({});

  const columns = useMemo(
    () =>
      data?.columns.map(({ id, displayName, uuidType }) => columnToCellMetaMapper({ id, displayName, uuidType })) ?? [],
    [data?.columns]
  );

  //   init react table
  const table = useReactTable({
    data: data?.values ?? [],
    columns,
    state: {
      expanded,
    },
    onExpandedChange: setExpanded,
    getSubRows: (row) => row.subRows,
    paginateExpandedRows: false,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getRowCanExpand: (row) => Boolean(row?.original['1']?.value?.reporteeCount),
    autoResetPageIndex: false,
    debugTable: true,
  });

  const renderRowSubComponent = React.useCallback(({ row, rowProps, visibleColumns }) => {
    const employeeId = row.original['1']?.uuid;
    const depth = 1;
    return (
      <SubGroupedByEmployeesTable
        employeeId={employeeId}
        row={row}
        rowProps={rowProps}
        visibleColumns={visibleColumns}
        depth={depth}
        setTableContainerWidth={setTableContainerWidth}
      />
    );
  }, []);

  useEffect(() => {
    table.setPageSize(rowsPerPageLS[groupedByEmployeesTableRowsPerPage] ?? defaultRowsPerPage);
  }, [rowsPerPageLS]);

  // resetting the table state when cohort changes, it's used to reset expanded rows on cohort change
  useEffect(() => {
    setExpanded({});
    table.reset();
  }, [sideNavSelectedCohort.id]);

  if (isLoading || isIdle) {
    return <Loader />;
  }
  return (
    <>
      <TableContainer style={{ overflow: 'auto' }}>
        <StyledTableContainer $tableContainerWidth={tableContainerWidth} style={{ overflow: 'visible' }}>
          <Table>
            <TableHead>
              {table.getHeaderGroups().map((headerGroup, index) => (
                <TableHeaderRow
                  key={headerGroup.id}
                  sx={{ backgroundColor: index === 1 ? theme.colors.neutral.EBEFF3 : 'inherit' }}
                >
                  {headerGroup.headers.map((header) => {
                    const { width, fixedWidth, align, borderLeft, marginLeft } = header?.column?.columnDef ?? {};
                    const justify = getFlexJustifyFromCellAlign(align);
                    return (
                      <TableHeaderCell
                        size="small"
                        variant="head"
                        width={width}
                        fixedWidth={fixedWidth}
                        // minWidth={minWidth}
                        key={header.id}
                        colSpan={header.colSpan}
                        justify={justify}
                        borderLeft={borderLeft}
                        sx={{
                          padding: index === 1 ? '6px' : '8px 16px !important',
                          div: { justifyContent: justify },
                        }}
                      >
                        {header.isPlaceholder ? null : (
                          <>{flexRender(header.column.columnDef.header, header.getContext())} </>
                        )}
                      </TableHeaderCell>
                    );
                  })}
                </TableHeaderRow>
              ))}
            </TableHead>
            <Tbody>
              {table.getRowModel().rows.map((row) => {
                const isRowExpanded = row?.getIsExpanded();

                return (
                  <React.Fragment key={row.id}>
                    <TableBodyRow
                      borderBottom={isRowExpanded ? '0 !important' : 'inherit'}
                      key={row.id}
                      bg={isRowExpanded ? 'neutral.F2F2F2' : 'initial'}
                    >
                      {' '}
                      {row.getVisibleCells().map((cell) => (
                        <Td key={cell.id} sx={{ position: 'relative', padding: '16px' }}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </Td>
                      ))}
                    </TableBodyRow>

                    {isRowExpanded && (
                      <TableBodyRow key={`${row.id}-expanded`} sx={{ borderBottom: '0 !important' }}>
                        <Td
                          style={{
                            padding: 0,
                            border: 'unset',
                          }}
                          colSpan={10} // <- used to control width of sub-table
                          sx={{
                            position: 'relative',
                            padding: '16px',
                          }}
                        >
                          <Collapse in={isRowExpanded} timeout="0" unmountOnExit>
                            {renderRowSubComponent({ row })}
                          </Collapse>
                        </Td>
                      </TableBodyRow>
                    )}
                  </React.Fragment>
                );
              })}
            </Tbody>
          </Table>
        </StyledTableContainer>
      </TableContainer>{' '}
      <Flex sx={{ justifyContent: 'flex-end' }}>
        <TablePagination
          rowsPerPageOptions={[10, 20, 30]}
          colSpan={0}
          count={data?.values?.length}
          rowsPerPage={rowsPerPageLS[groupedByEmployeesTableRowsPerPage] ?? defaultRowsPerPage}
          page={table.getState().pagination.pageIndex}
          SelectProps={{
            inputProps: { 'aria-label': 'rows per page' },
          }}
          onPageChange={(_, pageNum) => {
            table.setPageIndex(pageNum);
          }}
          handleChangeRowsPerPage={({ target }) => {
            setRowsPerPageLS({
              ...rowsPerPageLS,
              [groupedByEmployeesTableRowsPerPage]: target.value,
            });
            table.setPageSize(Number(target.value));
          }}
        />
      </Flex>
    </>
  );
};

const StyledTableContainer = styled(TableContainer)`
  min-width: ${(props) => props.$tableContainerWidth}px;
`;

export default GroupedByEmployeesTable;
